import { ChildApiDto, FilterApiDto } from '@b2x/storefront-api-js-client/src/dto';
import Cookies from 'js-cookie';

import { appConfig } from '../config';
import { env } from '../env';
import { addScript, slugify } from '../util';
import { AnalyticsEventsProps, InitCommonProps } from './analytics';
import {
  Event,
  EventParameters,
  fromProductSkusToItems,
  fromProductSkuToItem,
  fromProductSkuWithQuantityToItem,
  fromProductsSkusToItems,
} from './googleAnalytics4';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/ban-types
    dataLayer: Array<string | Object>;
  }
}

let initialized = false;
let debug = false;

interface InitProps extends InitCommonProps {
  customEvent?: {
    active?: boolean;
    customEventName?: string;
  };
  id?: string;
}
export type GoogleTagManagerInitProps = InitProps;

const init = (props?: InitProps): void => {
  const id = props?.id ?? env.REACT_APP_GTM_ID;
  debug = props?.debug || false;
  if (id) {
    if (!initialized) {
      initialized = true;
      install(id);
    }
  }
};

const install = (id: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  window.dataLayer = window.dataLayer || [];

  // Inizio gestione valori di default per Google Consent Mode v2
  if (appConfig.consentMode) {
    gtag('consent', 'default', {
      ad_personalization: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500,
    });
    gtag('set', 'ads_data_redaction', true);
    gtag('set', 'url_passthrough', true);
  }
  // Fine gestione valori di default per Google consent Mode v2

  const queryParams = env.REACT_APP_GTM_QUERY_PARAMS;

  addScript({
    id: 'gtm',
    text: `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl+'${queryParams ?? ''}';f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${id}');
    `,
  });
};

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions, func-style
function gtag(...args: Array<unknown>) {
  if (initialized) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
    log(args);
  } else {
    log('Blocked because not initialized.', args);
  }
}

const sendEvent = (event: Event, parameters?: EventParameters) => {
  if (initialized) {
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      ecommerce: parameters,
      event: event,
    });
    log(event, parameters);
  } else {
    log('Blocked because not initialized.', event, parameters);
  }
};

export interface CustomEventParameters {
  article_type?: string | null; //"Not an article related page" | puntuale
  business_model?: string | null;
  cdp_user_id?: string | null;
  content_health_condition?: string | null;
  content_life_stage?: string | null;
  content_pet_size?: string | null;
  content_pet_type?: string | null;
  content_special_needs?: string | null;
  //content_breed_type?: string; //undefined
  content_type?: string | null;
  ecosystem_category?: string | null;
  param_location?: string | null;
  product_category?: string | null;
  product_ingredient?: string | null;
  product_name?: string | null;
  product_sku?: string | null;
  range_brand?: string | null;
  user_ga_client_id?: string | null;
  user_pet_ownership?: string | null;
  user_pet_type?: string | null;
  //device_type?: string;
  //product_range?: string; // undefined
  //promotion_id?: string; // undefined
  //promotion_name?: string; // undefined
  //range_brand_id?: string; // es. "R000248" undefined
  //strategic_content_territory?: string;
  //website_section?: string;
}

const sendCustomEvent = (event: string, parameters?: CustomEventParameters) => {
  if (initialized) {
    window.dataLayer.push({
      event: event,
      ...parameters,
    });
    log(event, parameters);
  } else {
    log('Blocked because not initialized.', event, parameters);
  }
};

const getPetOwnershipType = (pets: Array<ChildApiDto>): string | null => {
  const types = new Set<string>();

  pets.forEach((pet) => {
    const addProps = pet.additionalProperties;
    if (addProps && addProps['PET_TYPE_CODE']) {
      const petTypeCode = addProps['PET_TYPE_CODE'];
      // Normalizziamo a uppercase per sicurezza
      types.add(petTypeCode.toUpperCase());
    }
  });

  const hasCat = types.has('CAT');
  const hasDog = types.has('DOG');

  if (hasCat && hasDog) return 'both';
  if (hasCat) return 'CAT';
  if (hasDog) return 'DOG';

  return null; // Nessuno animale trovato
};

const getCheckedFilterNames = (filters: Array<FilterApiDto>, code: string): string | null => {
  const filter = filters.find((f) => f.code === code);
  if (!filter?.children) return null;
  const names = filter.children.filter((child) => child.checkType === 'CHECKED').map((child) => child.name ?? '');
  return names.length > 0 ? names.join(', ') : null;
};

const log = (message?: unknown, ...optionalParams: Array<unknown>) => {
  if (debug) {
    console.log('analytics', 'gtm:', message, ...optionalParams);
  }
};

export const googleTagManager: {
  events: AnalyticsEventsProps;
  init(props?: InitProps): void;
} = {
  events: {
    addPaymentInfo: (props) => {
      sendEvent('add_payment_info', {
        coupon: props.coupon?.code,
        currency: 'EUR',
        items: props.products.map((product) => fromProductSkuWithQuantityToItem(product)),
        payment_type: props.paymentType,
        value: props.totalCost,
      });
    },
    addShippingInfo: (props) => {
      sendEvent('add_shipping_info', {
        coupon: props.coupon?.code,
        currency: 'EUR',
        items: props.products.map((product) => fromProductSkuWithQuantityToItem(product)),
        shipping_tier: props.shippingProfile,
        value: props.totalCost,
      });
    },
    addToCart: (props) => {
      sendEvent('add_to_cart', {
        currency: 'EUR',
        items: props.products.map((product) => fromProductSkuWithQuantityToItem(product)),
        value: props.totalPrice.value,
      });
    },
    addToWishlist: (props) => {
      sendEvent('add_to_wishlist', {
        currency: 'EUR',
        items: [fromProductSkuToItem(props.product)],
        value: props.product.sku.price.value,
      });
    },
    checkoutInit: (props) => {
      sendEvent('begin_checkout', {
        coupon: props.coupon?.code,
        currency: 'EUR',
        items: props.products.map((product) => fromProductSkuWithQuantityToItem(product)),
        value: props.totalCost,
      });
    },
    closePopup: (props) => {
      sendEvent('select_item', {
        items: [
          {
            item_category: 'popup',
            item_category2: 'close_action',
            item_id: props.popupId,
            item_name: props.popupCode,
          },
        ],
      });
    },
    completeRegistration: (props) => {
      sendEvent('sign_up', { method: props.origin });
    },
    contact: (props) => {
      // Assente in gtm
    },
    customizeProduct: (props) => {
      // Assente in gtm
    },
    donate: (props) => {
      // Assente in gtm
    },
    exception: (props) => {
      // Assente in gtm
    },
    findLocation: (props) => {
      // Assente in gtm
    },
    lead: (props) => {
      sendEvent('generate_lead', {
        // currency: '',
        // value: 0,
      });
    },
    login: (props) => {
      sendEvent('login', {
        method: props.method,
      });
    },
    maximizePopup: (props) => {
      sendEvent('select_item', {
        items: [
          {
            item_category: 'popup',
            item_category2: 'restore_action',
            item_id: props.popupId,
            item_name: props.popupCode,
          },
        ],
      });
    },
    minimizePopup: (props) => {
      sendEvent('select_item', {
        items: [
          {
            item_category: 'popup',
            item_category2: 'minimize_action',
            item_id: props.popupId,
            item_name: props.popupCode,
          },
        ],
      });
    },
    pageInformation: (props) => {
      const customEventName = appConfig.analytics?.googleTagManager?.customEvent?.customEventName;
      if (!appConfig.analytics?.googleTagManager?.customEvent?.active || !customEventName) return;

      const { product, routeInfo, searchProducts, session } = props;
      //console.log('GTM: ', props);

      const user_ga_client_id = Cookies.get('_ga');
      const ajs_user_id = Cookies.get('ajs_user_id');
      const ajs_anonymous_id = Cookies.get('ajs_anonymous_id');

      let product_name: string | null = null;
      let product_sku: string | null = null;
      let user_pet_type: string | null = null;
      let user_pet_ownership: 'yes' | 'no' | null = null;
      let content_life_stage: string | null = null;
      let content_pet_size: string | null = null;
      let content_special_needs: string | null = null;
      let product_ingredient: string | null = null;
      let range_brand: string | null = null;
      let content_pet_type: string | null = null;
      let content_health_condition: string | null = null;
      let product_category: string | null = null;
      let content_type: string | null = null;

      if (session?.customer?.children) {
        user_pet_type = getPetOwnershipType(session.customer.children);
        user_pet_ownership = user_pet_type ? 'yes' : 'no';
      }

      switch (routeInfo?.type) {
        case 'code':
          if (routeInfo.pathname === '/') {
            content_type = 'Home Page';
          } else if (routeInfo.pathname === '/search') {
            content_type = 'Search Query';
          }
          break;
        case 'template':
          if (routeInfo.pathname.startsWith('/magazine')) {
            content_type = 'Article Listing';
          }
          break;
        case 'content':
          content_type = 'Article';
          break;
        case 'product':
          content_type = 'Product';
          break;
        case 'listing':
          content_type = 'Product Listing';
          break;
      }

      if (product) {
        product_name = product.name ?? null;
        product_sku = product.skus?.at(0)?.eventSkuId ?? null;
        range_brand = product.brand?.name ?? null;
      }

      if (searchProducts) {
        const filters = searchProducts.filters;
        if (filters) {
          product_category = getCheckedFilterNames(filters, 'TEXTURE'); //da verificare!
          content_life_stage = getCheckedFilterNames(filters, 'FACE_ETA_CANE');
          content_pet_size = getCheckedFilterNames(filters, 'FACE_TAGLIA');
          content_special_needs = getCheckedFilterNames(filters, 'BISOGNI_SPECIFICI');
          product_ingredient = getCheckedFilterNames(filters, 'INGREDIENTI');
          range_brand = getCheckedFilterNames(filters, 'BRAND');
          content_pet_type = getCheckedFilterNames(filters, 'FACE_ANIMALE');
          content_health_condition = getCheckedFilterNames(filters, 'FACE_DIETE');
        }
      }

      sendCustomEvent(customEventName, {
        business_model: 'B2C',
        cdp_user_id: ajs_user_id || ajs_anonymous_id || null,
        content_health_condition: content_health_condition,
        content_life_stage: content_life_stage,
        content_pet_size: content_pet_size,
        content_pet_type: content_pet_type,
        content_special_needs: content_special_needs,
        content_type: content_type,
        ecosystem_category: 'business_core',
        param_location: 'page',
        product_category: product_category,
        product_ingredient: product_ingredient,
        product_name: product_name,
        product_sku: product_sku,
        range_brand: range_brand,
        user_ga_client_id: user_ga_client_id ?? null,
        user_pet_ownership: user_pet_ownership,
        user_pet_type: user_pet_type,
      });
    },
    pageLeave: (props) => {
      // Assente in gtm
    },
    pageView: (props) => {
      // Assente in gtm
    },
    purchase: (props) => {
      sendEvent('purchase', {
        coupon: props.coupon?.code,
        currency: 'EUR',
        items: props.products.map((product) => fromProductSkuWithQuantityToItem(product)),
        shipping: props.shippingCost,
        tax: props.taxCost,
        transaction_id: props.orderCode,
        value: props.totalCost,
      });
    },
    removeFromCart: (props) => {
      sendEvent('remove_from_cart', {
        currency: 'EUR',
        items: [fromProductSkuWithQuantityToItem(props.product)],
        value: props.product.sku.price.value,
      });
    },
    schedule: () => {
      // Assente in gtm
    },
    search: (props) => {
      sendEvent('search', {
        search_term: props.simpleSearch,
      });
    },
    selectProduct: (props) => {
      sendEvent('select_item', {
        item_list_id: slugify(props.listing.name),
        item_list_name: props.listing.name,
        items: fromProductSkusToItems(props.product),
      });
    },
    startPayment: (props) => {
      // Assente in gtm
    },
    submitApplication: () => {
      // Assente in gtm
    },
    viewCart: (props) => {
      sendEvent('view_cart', {
        currency: 'EUR',
        items: props.products.map((product) => fromProductSkuWithQuantityToItem(product)),
        value: props.totalCost,
      });
    },
    viewPopup: (props) => {
      sendEvent('view_item', {
        currency: 'EUR',
        items: [
          {
            item_category: 'popup',
            item_id: props.popupId,
            item_name: props.popupCode,
          },
        ],
        value: 0,
      });
    },
    viewProduct: (props) => {
      sendEvent('view_item', {
        currency: 'EUR',
        items: fromProductSkusToItems(props.product),
        value: props.product.fromPrice.value,
      });
    },
    viewProducts: (props) => {
      sendEvent('view_item_list', {
        currency: 'EUR',
        item_list_id: slugify(props.listing.name),
        item_list_name: props.listing.name,
        items: fromProductsSkusToItems(props.products),
      });
    },
  },
  init: (props) => {
    init(props);
  },
};
